// This hook owns every network call the confirm gate makes: the two reads
// (which contest is pendingOutcome, and its outcome detail) and the two
// writes (confirm, tiebreak-set). Refetching the outcome after a write is
// the generated mutation hook's own job (see useContestConfirm.ts and
// useContestTiebreakSet.ts's debounced query-cache refresh), so this hook
// never triggers that refresh itself and holds no query client reference.

import {
  useContestCancel,
  useContestConfirm,
  useContestList,
  useContestPendingOutcomeGet,
  useContestTiebreakSet,
  useRoundList,
} from "@sportsmet/contest-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";
import { useEffect, useRef } from "react";

import { useOverlay } from "../../routeState/useOverlay";
import {
  CONFIRM_GATE_KEEP_PLAYING_CANCEL_LABEL,
  CONFIRM_GATE_KEEP_PLAYING_CONFIRM_BODY,
  CONFIRM_GATE_KEEP_PLAYING_CONFIRM_LABEL,
  CONFIRM_GATE_KEEP_PLAYING_CONFIRM_TITLE,
} from "./confirmGateCopy";
import type {
  typeConfirmGateEntrant,
  typeConfirmGateView,
} from "./confirmGateState";
import { resolveConfirmGateView } from "./confirmGateState";
import type { typeAutoConfirmStatus } from "./confirmGateStatus";
import {
  resolveAutoConfirmStatus,
  resolveNamingWindowWarning,
} from "./confirmGateStatus";
import {
  isFailedEnvelope,
  mapPendingOutcome,
  raiseApiFailure,
  selectPendingContestId,
} from "./contestConfirmData";
import { CONTEST_CONFIRM_DIALOG_NAME } from "./contestConfirmRoute";

// ISSUE-868. `/round/list` pages by windowKey ascending, so the outstanding
// round is on the last page, not the first. 500 is the endpoint's own
// ROUND_LIST_PAGE_LIMIT_MAX, which is more rounds than a real season has, so
// one page is enough and this never follows nextCursor from inside a dialog.
const OUTSTANDING_ROUND_LOOKUP_LIMIT = 500;

export type typeContestConfirmGateModel =
  | { phase: "hidden" }
  | {
      phase: "ready";
      contestId: string;
      view: typeConfirmGateView;
      status: typeAutoConfirmStatus;
      namingWindowWarning: string | null;
      /** ISSUE-868. The outstanding round's label from `/round/list`, or null when it is not resolved. */
      outstandingRoundLabel: string | null;
      winners: readonly typeConfirmGateEntrant[];
      medalName: string;
      isDialogOpen: boolean;
      isConfirming: boolean;
      isSavingNamedWinners: boolean;
      /** ISSUE-1325. True while "keep playing" is on its way to the server. */
      isKeepingPlaying: boolean;
      /** ISSUE-1309. False on a pick'em contest: no entry is ever out. */
      canReviveInstead: boolean;
      confirm: () => void;
      notYet: () => void;
      /** ISSUE-1325. Cancels the computed end so play carries on. */
      keepPlaying: () => void;
      reviewAndConfirm: () => void;
      saveNamedWinners: (entryIds: readonly string[]) => void;
    };

export function useContestConfirmGate({
  poolId,
  nowMs,
  selectedContestId,
}: {
  poolId: string;
  nowMs?: number;
  selectedContestId?: string | undefined;
}): typeContestConfirmGateModel {
  const overlay = useOverlay();
  const list = useContestList({ poolId });
  const pendingContestId = selectPendingContestId(list.data);
  const contestId =
    selectedContestId === undefined || selectedContestId === pendingContestId
      ? pendingContestId
      : null;
  const pending = useContestPendingOutcomeGet(
    { poolId, contestId: contestId ?? "" },
    { enabled: contestId !== null },
  );
  const confirmMutation = useContestConfirm();
  const cancelMutation = useContestCancel();
  const tiebreakMutation = useContestTiebreakSet();
  const fetched = mapPendingOutcome(pending.data);

  const outstandingRoundId = fetched?.outcome.outstandingRound?.roundId ?? null;
  const rounds = useRoundList(
    {
      poolId,
      contestId: contestId ?? "",
      limit: OUTSTANDING_ROUND_LOOKUP_LIMIT,
    },
    { enabled: contestId !== null && outstandingRoundId !== null },
  );
  const outstandingRoundLabel =
    outstandingRoundId === null
      ? null
      : (rounds.data?.data?.items.find(
          (round) => round.roundId === outstandingRoundId,
        )?.label ?? null);

  const autoOpenedFor = useRef<string | null>(null);

  useEffect(() => {
    if (fetched === null || contestId === null) return;
    if (autoOpenedFor.current === contestId) return;
    if (overlay.overlay !== null) return;
    autoOpenedFor.current = contestId;
    overlay.open({ kind: "dialog", name: CONTEST_CONFIRM_DIALOG_NAME });
  }, [contestId, fetched, overlay]);

  if (fetched === null || contestId === null) {
    return { phase: "hidden" };
  }

  const resolvedNow = nowMs ?? Date.now();

  const confirm = (): void => {
    void (async () => {
      const result = await confirmMutation.mutateAsync({ poolId, contestId });
      if (isFailedEnvelope(result)) {
        raiseApiFailure(result);
        return;
      }
      overlay.close({ kind: "dialog" });
    })();
  };

  const notYet = (): void => {
    overlay.close({ kind: "dialog" });
  };

  // The server decides whether a cancel is allowed (the outcome must still be
  // pending); a refusal goes to the shared error modal like any other.
  const keepPlaying = (): void => {
    void (async () => {
      const isConfirmed = await confirmDestructive({
        title: CONFIRM_GATE_KEEP_PLAYING_CONFIRM_TITLE,
        body: CONFIRM_GATE_KEEP_PLAYING_CONFIRM_BODY,
        confirmLabel: CONFIRM_GATE_KEEP_PLAYING_CONFIRM_LABEL,
        cancelLabel: CONFIRM_GATE_KEEP_PLAYING_CANCEL_LABEL,
      });
      if (!isConfirmed) return;
      const result = await cancelMutation.mutateAsync({ poolId, contestId });
      if (isFailedEnvelope(result)) {
        raiseApiFailure(result);
        return;
      }
      overlay.close({ kind: "dialog" });
      void list.refetch();
    })();
  };

  const reviewAndConfirm = (): void => {
    overlay.open({ kind: "dialog", name: CONTEST_CONFIRM_DIALOG_NAME });
  };

  const saveNamedWinners = (entryIds: readonly string[]): void => {
    void (async () => {
      const result = await tiebreakMutation.mutateAsync({
        poolId,
        contestId,
        winnerEntryIds: [...entryIds],
      });
      if (isFailedEnvelope(result)) {
        raiseApiFailure(result);
        return;
      }
    })();
  };

  return {
    phase: "ready",
    contestId,
    view: resolveConfirmGateView(fetched.outcome),
    status: resolveAutoConfirmStatus({
      detectedAtMs: fetched.detectedAtMs,
      nudgeSentAtMs: fetched.nudgeSentAtMs,
      nowMs: resolvedNow,
    }),
    namingWindowWarning: resolveNamingWindowWarning({
      tiebreaker: fetched.outcome.tiebreaker,
      commissionerNamedAtMs: fetched.commissionerNamedAtMs,
      nowMs: resolvedNow,
    }),
    outstandingRoundLabel,
    winners: fetched.outcome.winners,
    medalName: fetched.outcome.medalName,
    isDialogOpen: overlay.isOpen("dialog", CONTEST_CONFIRM_DIALOG_NAME),
    isConfirming: confirmMutation.isPending,
    isSavingNamedWinners: tiebreakMutation.isPending,
    isKeepingPlaying: cancelMutation.isPending,
    canReviveInstead: fetched.outcome.isPickem !== true,
    confirm,
    notYet,
    keepPlaying,
    reviewAndConfirm,
    saveNamedWinners,
  };
}
